import type { LayoutSettingDescriptor } from "~/v3-poc/features/workspace/shared/layoutSettings/layoutSettings.types";

export const RGL_SETTING_IDS = [
  "cols",
  "rowHeight",
  "marginX",
  "marginY",
  "containerPaddingMode",
  "containerPaddingX",
  "containerPaddingY",
  "maxRowsLimited",
  "maxRows",
  "autoSize",
  "compactor",
  "bounded",
  "dragThreshold",
  "resizeHandles",
  "positionStrategy",
  "positionScale",
  "gridBounds",
  "containerBounds",
  "boundedX",
  "boundedY",
  "minMaxSize",
  "globalMinEnabled",
  "globalMinW",
  "globalMinH",
  "globalMaxEnabled",
  "globalMaxW",
  "globalMaxH",
  "snapEnabled",
  "snapX",
  "snapY",
  "aspectEnabled",
  "aspectRatio",
  "externalDropEnabled",
  "externalDropW",
  "externalDropH",
] as const;

export type RglSettingId = (typeof RGL_SETTING_IDS)[number];

const EXTERNAL_DROP_REASON = "Layout Labs has no external draggable source.";

export const REACT_GRID_LAYOUT_SCHEMA: Record<
  RglSettingId,
  LayoutSettingDescriptor
> = {
  cols: {
    id: "cols",
    label: "Columns",
    description: "Number of columns in the grid.",
    status: "live",
    control: { kind: "number", min: 1, max: 48, step: 1, integer: true },
  },
  rowHeight: {
    id: "rowHeight",
    label: "Row height",
    description: "Height of a single row in pixels.",
    status: "live",
    control: { kind: "number", min: 10, max: 400, step: 1, integer: true },
  },
  marginX: {
    id: "marginX",
    label: "Margin X",
    description: "Horizontal gap between grid items in pixels.",
    status: "live",
    control: { kind: "number", min: 0, max: 100, step: 1, integer: true },
  },
  marginY: {
    id: "marginY",
    label: "Margin Y",
    description: "Vertical gap between grid items in pixels.",
    status: "live",
    control: { kind: "number", min: 0, max: 100, step: 1, integer: true },
  },
  containerPaddingMode: {
    id: "containerPaddingMode",
    label: "Container padding mode",
    description:
      "Whether the container padding mirrors the margin or uses explicit custom values.",
    status: "live",
    control: {
      kind: "select",
      options: [
        { value: "margin", label: "Same as margin" },
        { value: "custom", label: "Custom" },
      ],
    },
  },
  containerPaddingX: {
    id: "containerPaddingX",
    label: "Container padding X",
    description:
      "Horizontal padding inside the grid container in pixels (custom mode only).",
    status: "live",
    control: { kind: "number", min: 0, max: 100, step: 1, integer: true },
  },
  containerPaddingY: {
    id: "containerPaddingY",
    label: "Container padding Y",
    description:
      "Vertical padding inside the grid container in pixels (custom mode only).",
    status: "live",
    control: { kind: "number", min: 0, max: 100, step: 1, integer: true },
  },
  maxRowsLimited: {
    id: "maxRowsLimited",
    label: "Limit max rows",
    description:
      "Enable to cap the number of rows. Disable for an unbounded grid.",
    status: "live",
    control: { kind: "boolean" },
  },
  maxRows: {
    id: "maxRows",
    label: "Max rows",
    description: "Maximum number of rows when limit is enabled.",
    status: "live",
    control: { kind: "number", min: 1, max: 10000, step: 1, integer: true },
  },
  autoSize: {
    id: "autoSize",
    label: "Auto-size height",
    description: "Automatically grow the container height to fit all items.",
    status: "live",
    control: { kind: "boolean" },
  },
  compactor: {
    id: "compactor",
    label: "Compactor",
    description: "Strategy used to compact items after a drag or resize.",
    status: "live",
    control: {
      kind: "select",
      options: [
        { value: "vertical", label: "Vertical" },
        { value: "horizontal", label: "Horizontal" },
        { value: "none", label: "None" },
        { value: "vertical-overlap", label: "Vertical (allow overlap)" },
        { value: "horizontal-overlap", label: "Horizontal (allow overlap)" },
        { value: "none-overlap", label: "None (allow overlap)" },
      ],
    },
  },
  bounded: {
    id: "bounded",
    label: "Bounded drag",
    description: "Prevent items from being dragged outside the container.",
    status: "live",
    control: { kind: "boolean" },
  },
  dragThreshold: {
    id: "dragThreshold",
    label: "Drag threshold",
    description: "Minimum pixel movement before a drag operation begins.",
    status: "live",
    control: { kind: "number", min: 0, max: 30, step: 1, integer: true },
  },
  resizeHandles: {
    id: "resizeHandles",
    label: "Resize handles",
    description: "Which directional handles are shown on resizable items.",
    status: "live",
    control: {
      kind: "handles",
      options: [
        { value: "n", label: "N" },
        { value: "ne", label: "NE" },
        { value: "e", label: "E" },
        { value: "se", label: "SE" },
        { value: "s", label: "S" },
        { value: "sw", label: "SW" },
        { value: "w", label: "W" },
        { value: "nw", label: "NW" },
      ],
    },
  },
  positionStrategy: {
    id: "positionStrategy",
    label: "Position strategy",
    description:
      "CSS strategy used to position items: CSS transforms, top/left, or scaled transforms.",
    status: "live",
    control: {
      kind: "select",
      options: [
        { value: "transform", label: "Transform" },
        { value: "absolute", label: "Absolute" },
        { value: "scaled", label: "Scaled" },
      ],
    },
  },
  positionScale: {
    id: "positionScale",
    label: "Position scale",
    description:
      "Scale factor applied to drag/resize coordinates when using the scaled strategy.",
    status: "live",
    control: { kind: "number", min: 0.1, max: 5, step: 0.05, integer: false },
  },
  gridBounds: {
    id: "gridBounds",
    label: "Grid bounds constraint",
    description: "Prevent items from moving outside the column/row boundaries.",
    status: "live",
    control: { kind: "boolean" },
  },
  containerBounds: {
    id: "containerBounds",
    label: "Container bounds constraint",
    description:
      "Constrain items to the visible container height instead of maxRows.",
    status: "live",
    control: { kind: "boolean" },
  },
  boundedX: {
    id: "boundedX",
    label: "Bounded X constraint",
    description:
      "Constrain horizontal movement only; vertical movement remains free.",
    status: "live",
    control: { kind: "boolean" },
  },
  boundedY: {
    id: "boundedY",
    label: "Bounded Y constraint",
    description:
      "Constrain vertical movement only; horizontal movement remains free.",
    status: "live",
    control: { kind: "boolean" },
  },
  minMaxSize: {
    id: "minMaxSize",
    label: "Per-item min/max size constraint",
    description:
      "Enforce per-item minW/maxW/minH/maxH properties during resize.",
    status: "live",
    control: { kind: "boolean" },
  },
  globalMinEnabled: {
    id: "globalMinEnabled",
    label: "Global minimum size",
    description: "Apply a grid-wide minimum width and height to all items.",
    status: "live",
    control: { kind: "boolean" },
  },
  globalMinW: {
    id: "globalMinW",
    label: "Global min width",
    description:
      "Minimum width in grid units applied to every item when global minimum is enabled.",
    status: "live",
    control: { kind: "number", min: 1, max: 48, step: 1, integer: true },
  },
  globalMinH: {
    id: "globalMinH",
    label: "Global min height",
    description:
      "Minimum height in grid units applied to every item when global minimum is enabled.",
    status: "live",
    control: { kind: "number", min: 1, max: 200, step: 1, integer: true },
  },
  globalMaxEnabled: {
    id: "globalMaxEnabled",
    label: "Global maximum size",
    description: "Apply a grid-wide maximum width and height to all items.",
    status: "live",
    control: { kind: "boolean" },
  },
  globalMaxW: {
    id: "globalMaxW",
    label: "Global max width",
    description:
      "Maximum width in grid units applied to every item when global maximum is enabled.",
    status: "live",
    control: { kind: "number", min: 1, max: 48, step: 1, integer: true },
  },
  globalMaxH: {
    id: "globalMaxH",
    label: "Global max height",
    description:
      "Maximum height in grid units applied to every item when global maximum is enabled.",
    status: "live",
    control: { kind: "number", min: 1, max: 200, step: 1, integer: true },
  },
  snapEnabled: {
    id: "snapEnabled",
    label: "Snap to grid",
    description:
      "Snap item positions to multiples of the specified step values during drag.",
    status: "live",
    control: { kind: "boolean" },
  },
  snapX: {
    id: "snapX",
    label: "Snap X step",
    description: "Horizontal snap step in grid units.",
    status: "live",
    control: { kind: "number", min: 1, max: 24, step: 1, integer: true },
  },
  snapY: {
    id: "snapY",
    label: "Snap Y step",
    description: "Vertical snap step in grid units.",
    status: "live",
    control: { kind: "number", min: 1, max: 200, step: 1, integer: true },
  },
  aspectEnabled: {
    id: "aspectEnabled",
    label: "Aspect ratio lock",
    description: "Lock a fixed pixel width-to-height ratio during resize.",
    status: "live",
    control: { kind: "boolean" },
  },
  aspectRatio: {
    id: "aspectRatio",
    label: "Aspect ratio",
    description:
      "Target width-to-height ratio in pixels (e.g. 1.777 for 16:9).",
    status: "live",
    control: { kind: "number", min: 0.1, max: 10, step: 0.01, integer: false },
  },
  externalDropEnabled: {
    id: "externalDropEnabled",
    label: "External drop",
    description: "Allow dragging external elements onto the grid.",
    status: "unavailable",
    control: { kind: "boolean" },
    unavailableReason: EXTERNAL_DROP_REASON,
  },
  externalDropW: {
    id: "externalDropW",
    label: "External drop width",
    description: "Default width in grid units for externally dropped items.",
    status: "unavailable",
    control: { kind: "number", min: 1, max: 48, step: 1, integer: true },
    unavailableReason: EXTERNAL_DROP_REASON,
  },
  externalDropH: {
    id: "externalDropH",
    label: "External drop height",
    description: "Default height in grid units for externally dropped items.",
    status: "unavailable",
    control: { kind: "number", min: 1, max: 200, step: 1, integer: true },
    unavailableReason: EXTERNAL_DROP_REASON,
  },
};
